/**
 * SEO ve yapay zekâ charts (v0.1.209, Sercan 19 Oct 2026: "grafiklerde istiyorum"): Google clicks and impressions week
 * by week (two charts, one measure each, never a second axis) and the weekly AI check, the questions each engine found
 * us in. Plain SVG; the tables below the charts carry the same numbers.
 *
 * Chart rules of the panel audit (25 Oct 2026): the takeaway is the chart's first line, the highest and the last week
 * carry their value on the plot, a tap, a click or the arrow keys pick a week and the readout row under the chart says
 * it (the last week until then), dates read "28 Eyl", clicks and impressions keep one colour on every page.
 */

import { SEO_ENGINE_LABELS, type SeoEngine, type SeoSearchWeek, type SeoWeek } from '../api/seo-report.js';
import { formatIsoDay, formatNumber } from '../lib/format.js';
import { ChartFrame, ChartReadout, ChartTakeaway, labelledPoints, pickHandlers, useChartPick } from './chart-kit.js';

const W = 360;
const H = 170;
const PAD = { l: 34, r: 12, t: 16, b: 24 };
/** At most this many week labels on an axis; the last week always has one. */
const MAX_X_LABELS = 6;
/** Line-end labels closer than this (viewBox units) are pushed apart. */
const LABEL_GAP = 11;
/** Fixed series order of the AI engines: the colour follows the engine, never its rank. */
const ENGINE_SERIES: Readonly<Record<SeoEngine, string>> = Object.freeze({
  gemini: 'var(--series-1)',
  brave: 'var(--series-2)',
  google: 'var(--series-3)',
});
/** The line-end label: short, so it fits beside the last point; the legend carries the full name. */
const ENGINE_SHORT: Readonly<Record<SeoEngine, string>> = Object.freeze({
  gemini: 'Yapay zekâ',
  brave: 'Brave',
  google: 'Google',
});

/** One metric, one colour on every page (tokens.css --metric-*). */
const MEASURE_CLASS: Readonly<Record<'clicks' | 'impressions', string>> = Object.freeze({
  clicks: 'seo-chart__bar--clicks',
  impressions: 'seo-chart__bar--impressions',
});

/** A round top for the y axis: 4, then 1, 2, 5 times a power of ten (a half like 2,5 prints its decimal). */
export function niceMax(value: number): number {
  if (value <= 4) return 4;
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) if (step * power >= value) return step * power;
  return 10 * power;
}

/** A tick's text: a whole number without decimals, anything else with one. */
export function tickText(value: number): string {
  return formatNumber(value, Number.isInteger(value) ? 0 : 1);
}

/** The Monday (ISO date) of the week holding `day`. */
export function mondayOf(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return date.toISOString().slice(0, 10);
}

/** The indexes that get an axis label: every stride-th week counted back from the last, so the last always has one. */
export function labelIndexes(count: number, max: number = MAX_X_LABELS): Set<number> {
  const stride = Math.max(1, Math.ceil(count / max));
  const out = new Set<number>();
  for (let index = count - 1; index >= 0; index -= stride) out.add(index);
  return out;
}

/** Line-end label positions sorted top to bottom and pushed at least `gap` apart, kept inside [top, bottom]. */
export function spreadLabels<T extends { y: number }>(
  labels: readonly T[],
  top: number,
  bottom: number,
  gap = LABEL_GAP,
): T[] {
  const sorted = [...labels].sort((a, b) => a.y - b.y).map((label) => ({ ...label }));
  for (let i = 1; i < sorted.length; i += 1) {
    const before = sorted[i - 1];
    const here = sorted[i];
    if (before !== undefined && here !== undefined && here.y - before.y < gap) here.y = before.y + gap;
  }
  // Pushed past the bottom: shift the whole stack up, never above the top.
  const last = sorted.at(-1);
  const over = last === undefined ? 0 : last.y - bottom;
  if (over > 0) for (const label of sorted) label.y = Math.max(top, label.y - over);
  return sorted;
}

/** The SVG path of points by week index; a missing week starts a new segment instead of bridging the gap. */
export function segmentedPath(points: readonly { index: number; x: number; y: number }[]): string {
  return points
    .map((point, i) => {
      const before = points[i - 1];
      const move = before === undefined || point.index - before.index > 1;
      return `${move ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
    })
    .join(' ');
}

/**
 * "son tam hafta 12 tıklama (önceki 9)": the last finished week against the one before; the running week is not
 * compared, it is still filling.
 */
export function weeklyTakeaway(
  weeks: readonly SeoSearchWeek[],
  measure: 'clicks' | 'impressions',
  unit: string,
  running: string | null,
): string {
  const done = weeks.filter((week) => week.week !== running);
  const last = done.at(-1);
  if (last === undefined) return `İlk hafta sürüyor: ${formatNumber(weeks.at(-1)?.[measure] ?? 0)} ${unit}`;
  const before = done.at(-2);
  const tail = before === undefined ? '' : ` (önceki ${formatNumber(before[measure])})`;
  return `Son tam hafta ${formatNumber(last[measure])} ${unit}${tail}`;
}

/**
 * One measure of the weekly search totals as bars. The week holding `to` (the report's last day) is still running and
 * is drawn lighter; a week with no rows yet is simply not there.
 */
export function WeeklyBars({
  title,
  weeks,
  measure,
  unit,
  to,
}: {
  title: string;
  weeks: readonly SeoSearchWeek[];
  measure: 'clicks' | 'impressions';
  unit: string;
  to: string | null;
}) {
  const pick = useChartPick(weeks.length);
  if (weeks.length === 0) return <p className="sx-empty">Bu dönemde yok.</p>;
  const running = to === null ? null : mondayOf(to);
  const top = niceMax(Math.max(...weeks.map((week) => week[measure])));
  const slot = (W - PAD.l - PAD.r) / weeks.length;
  const bar = Math.max(4, Math.min(28, slot - 6));
  const y = (value: number) => H - PAD.b - ((H - PAD.t - PAD.b) * value) / top;
  const labelled = labelIndexes(weeks.length);
  const valued = new Set(labelledPoints(weeks.map((week) => week[measure])));
  const total = weeks.reduce((sum, week) => sum + week[measure], 0);
  const shown = weeks[pick.index];
  const hasRunning = weeks.some((week) => week.week === running);
  const readout =
    shown === undefined
      ? ''
      : `${formatIsoDay(shown.week)} haftası${shown.week === running ? ' (sürüyor)' : ''}: ${formatNumber(shown.clicks)} tıklama · ${formatNumber(shown.impressions)} gösterim · ${shown.position === null ? 'ortalama sıra yok' : `ortalama sıra ${formatNumber(shown.position, 1)}`}`;
  return (
    <figure className="seo-chart">
      <ChartTakeaway>{weeklyTakeaway(weeks, measure, unit, running)}</ChartTakeaway>
      <ChartFrame label={`${title}: hafta seç`} count={weeks.length} pick={pick} valueText={readout}>
        <svg
          className="seo-chart__svg"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={`${title}: ${weeks.length} haftada toplam ${formatNumber(total)} ${unit}`}
        >
          {[0, top / 2, top].map((tick) => (
            <g key={tick}>
              <line className="seo-chart__grid" x1={PAD.l} x2={W - PAD.r} y1={y(tick)} y2={y(tick)} />
              <text className="seo-chart__tick" x={PAD.l - 6} y={y(tick) + 3} textAnchor="end">
                {tickText(tick)}
              </text>
            </g>
          ))}
          {weeks.map((week, index) => {
            const cx = PAD.l + slot * index + slot / 2;
            const value = week[measure];
            const partial = week.week === running;
            const picked = pick.index === index;
            return (
              <g key={week.week}>
                <rect
                  className={picked ? 'seo-chart__hit is-picked' : 'seo-chart__hit'}
                  x={cx - slot / 2}
                  y={PAD.t}
                  width={slot}
                  height={H - PAD.t - PAD.b}
                  {...pickHandlers(pick, index)}
                />
                {value === 0 ? null : (
                  <rect
                    className={`seo-chart__bar ${MEASURE_CLASS[measure]}${partial ? ' seo-chart__bar--partial' : ''}`}
                    x={cx - bar / 2}
                    y={y(value)}
                    width={bar}
                    height={Math.max(H - PAD.b - y(value), 1)}
                    rx={Math.min(2, bar / 2)}
                    pointerEvents="none"
                  />
                )}
                {valued.has(index) ? (
                  <text className="seo-chart__value" x={cx} y={y(value) - 4} textAnchor="middle">
                    {formatNumber(value)}
                  </text>
                ) : null}
                {labelled.has(index) ? (
                  <text className="seo-chart__tick" x={cx} y={H - 8} textAnchor="middle">
                    {formatIsoDay(week.week)}
                  </text>
                ) : null}
              </g>
            );
          })}
          <line className="seo-chart__axis" x1={PAD.l} x2={W - PAD.r} y1={H - PAD.b} y2={H - PAD.b} />
        </svg>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
      {hasRunning ? (
        <figcaption className="seo-chart__note">Açık renkli son çubuk bu hafta: henüz bitmedi.</figcaption>
      ) : null}
    </figure>
  );
}

/** The weekly AI check: per engine, how many of the asked questions found us; one line per engine. */
export function AiFoundLines({
  weeks,
  summary,
  engines,
}: {
  weeks: readonly string[];
  summary: readonly SeoWeek[];
  engines: readonly SeoEngine[];
}) {
  const pick = useChartPick(weeks.length);
  if (weeks.length === 0 || engines.length === 0) return <p className="sx-empty">Henüz test yok.</p>;
  const row = (week: string, engine: SeoEngine) =>
    summary.find((item) => item.weekStart === week && item.engine === engine);
  const top = niceMax(Math.max(1, ...summary.map((item) => item.asked)));
  const step = weeks.length > 1 ? (W - PAD.l - PAD.r - 86) / (weeks.length - 1) : 0;
  const x = (index: number) => PAD.l + 8 + step * index;
  const y = (value: number) => H - PAD.b - ((H - PAD.t - PAD.b) * value) / top;
  const labelled = labelIndexes(weeks.length);
  const series = engines.map((engine) => ({
    engine,
    points: weeks.flatMap((week, index) => {
      const item = row(week, engine);
      return item === undefined || item.asked === 0
        ? []
        : [{ index, found: item.found, x: x(index), y: y(item.found) }];
    }),
  }));
  const ends = spreadLabels(
    series.flatMap((s) => {
      const end = s.points.at(-1);
      return end === undefined ? [] : [{ engine: s.engine, x: end.x, y: end.y + 3, found: end.found }];
    }),
    PAD.t + 3,
    H - PAD.b,
  );
  // The best week per engine gets its value too, when it is not the last one (the line end says that).
  const peaks = series.flatMap((s) => {
    if (s.points.length < 2) return [];
    const best = s.points.reduce((a, b) => (b.found > a.found ? b : a));
    return best.index === s.points.at(-1)?.index || best.found === 0 ? [] : [{ engine: s.engine, ...best }];
  });
  const shownWeek = weeks[pick.index];
  const readout =
    shownWeek === undefined
      ? ''
      : `${formatIsoDay(shownWeek)} haftası: ${engines
          .map((engine) => {
            const item = row(shownWeek, engine);
            return item === undefined || item.asked === 0
              ? `${ENGINE_SHORT[engine]} cevap yok`
              : `${ENGINE_SHORT[engine]} ${formatNumber(item.found)} / ${formatNumber(item.asked)}`;
          })
          .join(' · ')}`;
  const lastWeek = weeks.at(-1);
  const leader = series
    .map((s) => ({
      engine: s.engine,
      found: s.points.at(-1)?.index === weeks.length - 1 ? (s.points.at(-1)?.found ?? 0) : 0,
    }))
    .reduce((a, b) => (b.found > a.found ? b : a), { engine: engines[0] as SeoEngine, found: 0 });
  const askedLast =
    lastWeek === undefined ? 0 : Math.max(0, ...engines.map((engine) => row(lastWeek, engine)?.asked ?? 0));
  return (
    <figure className="seo-chart">
      <ChartTakeaway>
        {leader.found === 0
          ? 'Son testte hiçbir soruda çıkmadık'
          : `Son test: en çok ${ENGINE_SHORT[leader.engine]}, ${formatNumber(leader.found)} / ${formatNumber(askedLast)} soruda`}
      </ChartTakeaway>
      <ul className="seo-chart__legend" aria-label="Arama motorları">
        {engines.map((engine) => (
          <li key={engine}>
            <span className="seo-chart__swatch" style={{ background: ENGINE_SERIES[engine] }} aria-hidden="true" />
            {SEO_ENGINE_LABELS[engine]}
          </li>
        ))}
      </ul>
      <ChartFrame label="Yapay zekâ testi: hafta seç" count={weeks.length} pick={pick} valueText={readout}>
        <svg
          className="seo-chart__svg"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={`Yapay zekâ testi, ${weeks.length} hafta: ${series
            .map((s) => `${SEO_ENGINE_LABELS[s.engine]} son hafta ${formatNumber(s.points.at(-1)?.found ?? 0)}`)
            .join(', ')}`}
        >
          {[0, top / 2, top].map((tick) => (
            <g key={tick}>
              <line className="seo-chart__grid" x1={PAD.l} x2={W - PAD.r} y1={y(tick)} y2={y(tick)} />
              <text className="seo-chart__tick" x={PAD.l - 6} y={y(tick) + 3} textAnchor="end">
                {tickText(tick)}
              </text>
            </g>
          ))}
          {pick.index < 0 ? null : (
            <line className="seo-chart__cross" x1={x(pick.index)} x2={x(pick.index)} y1={PAD.t} y2={H - PAD.b} />
          )}
          {series.map((s) => (
            <g key={s.engine} style={{ color: ENGINE_SERIES[s.engine] }} pointerEvents="none">
              {s.points.length > 1 ? <path className="seo-chart__line" d={segmentedPath(s.points)} /> : null}
              {s.points.map((point) => (
                <circle
                  key={point.index}
                  className="seo-chart__dot"
                  cx={point.x}
                  cy={point.y}
                  r={pick.index === point.index ? 5 : 4}
                />
              ))}
            </g>
          ))}
          {peaks.map((peak) => (
            <text key={peak.engine} className="seo-chart__value" x={peak.x} y={peak.y - 8} textAnchor="middle">
              {formatNumber(peak.found)}
            </text>
          ))}
          {ends.map((end) => (
            <text key={end.engine} className="seo-chart__label" x={end.x + 9} y={end.y}>
              {ENGINE_SHORT[end.engine]} {formatNumber(end.found)}
            </text>
          ))}
          {weeks.map((week, index) => (
            <g key={week}>
              <rect
                className="seo-chart__hit"
                x={x(index) - Math.max(step, 24) / 2}
                y={PAD.t}
                width={Math.max(step, 24)}
                height={H - PAD.t - PAD.b}
                {...pickHandlers(pick, index)}
              />
              {labelled.has(index) ? (
                <text className="seo-chart__tick" x={x(index)} y={H - 8} textAnchor="middle">
                  {formatIsoDay(week)}
                </text>
              ) : null}
            </g>
          ))}
          <line className="seo-chart__axis" x1={PAD.l} x2={W - PAD.r} y1={H - PAD.b} y2={H - PAD.b} />
        </svg>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
      <figcaption className="seo-chart__note">
        Her pazartesi aynı alıcı soruları soruluyor; çizgi, kaç soruda sitemizin anıldığı ya da ilk sonuçlarda çıktığı.
      </figcaption>
    </figure>
  );
}
